import { memo, useState } from 'react';
import { X } from 'lucide-react';
import { EarlyWarning, Note } from '@/types/early-warning';

interface EarlyWarningFormModalProps {
  selectedWarning: EarlyWarning | null;
  onClose: () => void;
  onSave: (warning: EarlyWarning) => void;
}

export const EarlyWarningFormModal = memo(
  ({ selectedWarning, onClose, onSave }: EarlyWarningFormModalProps) => {
    const [activeTab, setActiveTab] = useState<'details' | 'notes'>('details');
    const [severity, setSeverity] = useState(selectedWarning?.severity || 1);
    const [likelihood, setLikelihood] = useState(
      selectedWarning?.likelihood || 1
    );
    const [noteText, setNoteText] = useState('');
    const [notes, setNotes] = useState<Note[]>(selectedWarning?.notes || []);

    const riskScore = severity * likelihood;

    const handleAddNote = () => {
      if (!noteText.trim()) return;
      const newNote: Note = {
        id: Date.now(),
        text: noteText,
        author: 'Current User',
        date: new Date().toISOString(),
      };
      setNotes([...notes, newNote]);
      setNoteText('');
    };

    const handleSaveWarning = () => {
      const title = (document.getElementById('title') as HTMLInputElement)
        ?.value;
      const reference = (
        document.getElementById('reference') as HTMLInputElement
      )?.value;
      const description = (
        document.getElementById('description') as HTMLTextAreaElement
      )?.value;
      const notifiedBy = (
        document.getElementById('notified-by') as HTMLInputElement
      )?.value;
      const email = (document.getElementById('email') as HTMLInputElement)
        ?.value;
      const notificationDate = (
        document.getElementById('notification-date') as HTMLInputElement
      )?.value;
      const replyDue = (
        document.getElementById('reply-due') as HTMLInputElement
      )?.value;

      const increasePrices = (
        document.getElementById('impact1') as HTMLInputElement
      )?.checked;
      const delayCompletion = (
        document.getElementById('impact2') as HTMLInputElement
      )?.checked;
      const delayKeyDate = (
        document.getElementById('impact3') as HTMLInputElement
      )?.checked;
      const impairPerformance = (
        document.getElementById('impact4') as HTMLInputElement
      )?.checked;
      const increaseContractorCost = (
        document.getElementById('impact5') as HTMLInputElement
      )?.checked;

      const riskStatusAvoided = (
        document.getElementById('avoided') as HTMLInputElement
      )?.checked;
      const replyDueDate = new Date(replyDue);
      const currentDate = new Date();

      let status: 'ACTIVE' | 'OVERDUE' | 'AVOIDED' = 'ACTIVE';
      if (riskStatusAvoided) {
        status = 'AVOIDED';
      } else if (replyDueDate < currentDate) {
        status = 'OVERDUE';
      }

      const newWarning: EarlyWarning = {
        id: selectedWarning ? selectedWarning.id : `EW-NEW`,
        reference: reference || '',
        role: notifiedBy || '',
        title: title || '',
        description: description || '',
        replyDue: replyDue || '',
        replyDate: '',
        email: email || '',
        notificationDate: notificationDate || '',
        severity,
        likelihood,
        score: riskScore,
        status,
        impacts: {
          increasePrices: increasePrices || false,
          delayCompletion: delayCompletion || false,
          delayKeyDate: delayKeyDate || false,
          impairPerformance: impairPerformance || false,
          increaseContractorCost: increaseContractorCost || false,
        },
        riskStatus: riskStatusAvoided ? 'Avoided' : 'Present',
        notes,
      };

      onSave(newWarning);
    };

    return (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {selectedWarning ? 'Edit Early Warning' : 'New Early Warning'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="border-b border-gray-200 mb-6">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'details'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`px-4 py-2 text-sm font-medium transition ${
                    activeTab === 'notes'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Notes
                </button>
              </div>
            </div>
            <div className="min-h-[60vh]">
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium mb-1"
                    >
                      Title
                    </label>
                    <input
                      id="title"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={selectedWarning?.title || ''}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="reference"
                      className="block text-sm font-medium mb-1"
                    >
                      Reference
                    </label>
                    <input
                      id="reference"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={selectedWarning?.reference || ''}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium mb-1"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={selectedWarning?.description || ''}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="notified-by"
                      className="block text-sm font-medium mb-1"
                    >
                      Notified By
                    </label>
                    <input
                      id="notified-by"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={selectedWarning?.role || ''}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium mb-1"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={selectedWarning?.email || ''}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="notification-date"
                      className="block text-sm font-medium mb-1"
                    >
                      Notification Date
                    </label>
                    <input
                      id="notification-date"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={
                        selectedWarning?.notificationDate ||
                        new Date().toISOString().split('T')[0]
                      }
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="reply-due"
                      className="block text-sm font-medium mb-1"
                    >
                      Reply Due
                    </label>
                    <input
                      id="reply-due"
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      defaultValue={
                        selectedWarning?.replyDue ||
                        (() => {
                          const date = new Date();
                          date.setDate(date.getDate() + 14);
                          return date.toISOString().split('T')[0];
                        })()
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Impacts</h3>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="impact1"
                        defaultChecked={
                          selectedWarning?.impacts?.increasePrices || false
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Increase Prices</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="impact2"
                        defaultChecked={
                          selectedWarning?.impacts?.delayCompletion || false
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Delay Completion</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="impact3"
                        defaultChecked={
                          selectedWarning?.impacts?.delayKeyDate || false
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Delay Key Date</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="impact4"
                        defaultChecked={
                          selectedWarning?.impacts?.impairPerformance || false
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Impair Performance</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="impact5"
                        defaultChecked={
                          selectedWarning?.impacts?.increaseContractorCost ||
                          false
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">Increase Contractor Cost</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Risk Status</h3>
                    <div className="flex space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="risk-status"
                          id="present"
                          defaultChecked={
                            !selectedWarning ||
                            selectedWarning.riskStatus !== 'Avoided'
                          }
                          className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Risk Still Present</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="risk-status"
                          id="avoided"
                          defaultChecked={
                            selectedWarning?.riskStatus === 'Avoided'
                          }
                          className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Risk Avoided / Passed</span>
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="severity"
                        className="block text-sm font-medium mb-1"
                      >
                        Severity
                      </label>
                      <select
                        id="severity"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        value={severity}
                        onChange={(e) => setSeverity(parseInt(e.target.value))}
                      >
                        {[1, 2, 3, 4, 5].map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="likelihood"
                        className="block text-sm font-medium mb-1"
                      >
                        Likelihood
                      </label>
                      <select
                        id="likelihood"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        value={likelihood}
                        onChange={(e) =>
                          setLikelihood(parseInt(e.target.value))
                        }
                      >
                        {[1, 2, 3, 4, 5].map((val) => (
                          <option key={val} value={val}>
                            {val}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveWarning}
                      className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Save Early Warning
                    </button>
                  </div>
                </div>
              )}
              {activeTab === 'notes' && (
                <div>
                  <div className="space-y-4 mb-6 max-h-[50vh] overflow-y-auto p-4 border border-gray-200 rounded-lg">
                    {notes.length > 0 ? (
                      notes.map((note: Note) => (
                        <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-medium">{note.author}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(note.date).toLocaleString()}
                            </div>
                          </div>
                          <div className="text-sm">{note.text}</div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        No notes have been added yet
                      </div>
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <label
                      htmlFor="note-text"
                      className="block text-sm font-medium mb-2"
                    >
                      Add Note
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        id="note-text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        rows={3}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note about this early warning..."
                      />
                    </div>
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleAddNote}
                        disabled={!noteText.trim()}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          noteText.trim()
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Add Note
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleSaveWarning}
                      className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    >
                      Save Early Warning
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

EarlyWarningFormModal.displayName = 'EarlyWarningFormModal';
